import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { get, post, put } from "src/utils/networkUtils";

// Odds are a decoration on fixtures we already have: an operator points a
// tournament at a Midnite competition, pairs the fixtures, and refreshes prices.
//
// Every table below describes stored state, never a live recomputation. Matched
// and unmatched split on whether our match row carries a midnite_id — the exact
// condition the odds job polls on — and the Midnite side is what the last sync
// recorded. Nothing here calls the partner; only the Sync buttons do.

// The competition an unmapped tournament is pre-filled with. Held as a name,
// not an id: Midnite's ids differ between their test and production
// catalogues, so it is resolved against the list they return.
const DEFAULT_COMPETITION_NAME = "England Premier League";

const normaliseName = (name) => name.trim().replace(/\s+/g, " ").toLowerCase();

// Two entries of one name is ambiguity, and guessing between them could hang a
// second-tier competition's prices on our fixtures, so the operator picks.
const findDefaultCompetitionId = (competitions) => {
  const target = normaliseName(DEFAULT_COMPETITION_NAME);
  const hits = competitions.filter((c) => normaliseName(c.name ?? "") === target);
  return hits.length === 1 ? String(hits[0].id) : "";
};

const dayKey = (value) => new Date(value).toISOString().slice(0, 10);

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
      })
    : "-";

export default function MidniteOddsSection({ onRunComplete }) {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTcId, setSelectedTcId] = useState("");
  const [competitions, setCompetitions] = useState([]);
  const [competitionsError, setCompetitionsError] = useState(null);
  const [defaultCompetitionId, setDefaultCompetitionId] = useState("");
  const [selectedCompetitionId, setSelectedCompetitionId] = useState("");
  const [editing, setEditing] = useState(false);
  const [pairing, setPairing] = useState(null);
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(null); // 'save' | 'pair' | 'odds' | null
  const [feedback, setFeedback] = useState(null); // { severity, message }

  const selectedTournament = tournaments.find((t) => t.id === selectedTcId);

  const refreshTournaments = useCallback(async () => {
    try {
      const response = await get("/admin/midnite/tournaments");
      const items = response?.data ?? [];
      setTournaments(items);
      // Open on the season being played. Past seasons of the same competition
      // sit alongside it and carry no mapping, so opening on one of those is
      // what makes a configured tournament look unconfigured.
      setSelectedTcId(
        (current) => current || items.find((t) => t.status === "active")?.id || items[0]?.id || ""
      );
    } catch (err) {
      setTournaments([]);
    }
  }, []);

  const refreshPairing = useCallback(async (tcId) => {
    if (!tcId) {
      setPairing(null);
      return;
    }
    try {
      const response = await get(`/admin/midnite/tournaments/${tcId}/pairing`);
      setPairing(response?.data ?? null);
    } catch (err) {
      setPairing(null);
    }
  }, []);

  useEffect(() => {
    refreshTournaments();
  }, [refreshTournaments]);

  useEffect(() => {
    // The competition list is a partner call, so it is fetched once rather than
    // on every selection change.
    get("/admin/midnite/competitions")
      .then((response) => {
        const items = response?.data ?? [];
        setCompetitions(items);
        setDefaultCompetitionId(findDefaultCompetitionId(items));
        setCompetitionsError(null);
      })
      .catch((err) => {
        setCompetitions([]);
        setDefaultCompetitionId("");
        setCompetitionsError(
          err?.response?.data?.message ??
            "Could not reach Midnite. Check the backend's IP is registered."
        );
      });
  }, []);

  // A tournament that is already mapped opens locked, so changing a live
  // mapping is a deliberate act; an unmapped one opens on the default, ready
  // to save. Nothing here writes - only Save does.
  useEffect(() => {
    const stored = selectedTournament?.midniteCompetitionId ?? "";
    setSelectedCompetitionId(stored || defaultCompetitionId);
    setEditing(!stored);
  }, [selectedTournament, defaultCompetitionId]);

  useEffect(() => {
    refreshPairing(selectedTcId);
  }, [selectedTcId, refreshPairing]);

  const runAction = async (kind, action, successMessage) => {
    setBusy(kind);
    setFeedback(null);
    try {
      const data = await action();
      setFeedback({ severity: "success", message: successMessage(data) });
      await refreshTournaments();
      await refreshPairing(selectedTcId);
      if (onRunComplete) onRunComplete();
    } catch (err) {
      setFeedback({
        severity: "error",
        message: err?.response?.data?.message ?? err?.message ?? "The request failed",
      });
    } finally {
      setBusy(null);
    }
  };

  const saveCompetition = () =>
    runAction(
      "save",
      async () =>
        put(`/admin/midnite/tournaments/${selectedTcId}/competition`, {
          midniteCompetitionId: selectedCompetitionId || null,
        }),
      () =>
        selectedCompetitionId
          ? "Midnite competition saved. Run Sync matches to pair fixtures."
          : "Midnite competition cleared. Odds are now off for this tournament."
    );

  const runPairing = () =>
    runAction(
      "pair",
      async () => {
        const response = await post(`/admin/midnite/tournaments/${selectedTcId}/pair`, {});
        return response?.data ?? {};
      },
      (data) =>
        data.skipped
          ? `Skipped - ${data.reason}`
          : `Paired ${data.paired} of ${data.midniteFixtures} Midnite fixture(s); ${
              data.theirUnmatched?.length ?? 0
            } of theirs unmatched.`
    );

  const runOddsSync = () =>
    runAction(
      "odds",
      async () => {
        const response = await post("/admin/midnite/sync-odds", {});
        return response?.data ?? {};
      },
      (data) =>
        data.skipped
          ? `Skipped - ${data.reason}`
          : `Refreshed ${data.updated} fixture(s); ${data.halted} halted, ${data.notFound} not found, ${data.failed} failed, ${data.cleanedUp} cleaned up.`
    );

  const isConfigured = Boolean(selectedTournament?.midniteCompetitionId);

  const competitionHelperText = !editing
    ? "Saved - press Edit to change it"
    : isConfigured
      ? "Leave blank to switch odds off for this tournament"
      : "Not saved yet - press Save to apply it";

  const matchedFixtures = pairing?.matchedFixtures ?? [];
  const unmatchedFixtures = pairing?.unmatchedFixtures ?? [];
  const lastSync = pairing?.lastSync ?? null;
  const lastAttempt = pairing?.lastAttempt ?? null;
  const theirUnmatched = lastSync?.theirUnmatched ?? [];
  const syncWindow = lastSync?.window ?? null;

  // Runs logged before the window was recorded have none, and then no row can
  // be judged against it.
  const inMidniteWindow = (kickoffAt) => {
    if (!kickoffAt || !syncWindow?.from || !syncWindow?.to) return false;
    const day = dayKey(kickoffAt);
    return day >= dayKey(syncWindow.from) && day <= dayKey(syncWindow.to);
  };

  const windowLabel = syncWindow?.from
    ? `${formatDate(syncWindow.from)} - ${formatDate(syncWindow.to)}`
    : null;

  const syncCaption = lastSync
    ? `From the sync at ${formatDateTime(lastSync.startedAt)}${
        windowLabel ? ` - Midnite window ${windowLabel}` : ""
      }`
    : null;

  const attemptFailed =
    lastAttempt?.status === "failed" &&
    (!lastSync || new Date(lastAttempt.startedAt) > new Date(lastSync.startedAt));

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6">Odds (Midnite)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A tournament with no competition mapped is pre-filled with {DEFAULT_COMPETITION_NAME} -
          press Save to apply it, or pick another first. Then pair our fixtures with theirs and
          refresh prices. Fixtures that cannot be paired confidently show no odds strip - that is
          the designed fallback, not a fault.
        </Typography>

        {competitionsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {competitionsError}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <TextField
            select
            label="Tournament"
            value={selectedTcId}
            onChange={(e) => setSelectedTcId(e.target.value)}
            sx={{ minWidth: 280 }}
          >
            {tournaments.length === 0 && (
              <MenuItem value="" disabled>
                No tournaments yet
              </MenuItem>
            )}
            {tournaments.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.competitionName ?? "(unnamed)"} {t.seasonLabel ?? ""}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Midnite competition"
            value={selectedCompetitionId}
            onChange={(e) => setSelectedCompetitionId(e.target.value)}
            disabled={!editing || competitions.length === 0}
            helperText={competitionHelperText}
            sx={{ minWidth: 320 }}
          >
            <MenuItem value="">(none)</MenuItem>
            {competitions.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="outlined"
            onClick={editing ? saveCompetition : () => setEditing(true)}
            disabled={!selectedTcId || busy !== null}
            sx={{ mt: 1 }}
          >
            {busy === "save" ? <CircularProgress size={20} /> : editing ? "Save" : "Edit"}
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={runPairing}
            disabled={!isConfigured || busy !== null}
          >
            {busy === "pair" ? <CircularProgress size={20} /> : "Sync matches"}
          </Button>
          <Button
            variant="contained"
            onClick={runOddsSync}
            disabled={!isConfigured || busy !== null}
          >
            {busy === "odds" ? <CircularProgress size={20} /> : "Sync odds"}
          </Button>
        </Stack>

        {feedback && (
          <Alert severity={feedback.severity} sx={{ mb: 2 }}>
            {feedback.message}
          </Alert>
        )}

        {pairing && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip label={`${pairing.matched} matched`} color="success" size="small" />
              <Chip
                label={`${pairing.unmatched} unmatched`}
                color={pairing.unmatched > 0 ? "warning" : "default"}
                size="small"
              />
              <Chip label={`${pairing.total} upcoming fixtures`} size="small" />
            </Stack>

            {attemptFailed && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The last sync at {formatDateTime(lastAttempt.startedAt)} failed:{" "}
                {lastAttempt.error ?? "unknown error"}.
                {lastSync
                  ? " The Midnite list below is from the last successful sync."
                  : " Nothing has synced successfully yet."}
              </Alert>
            )}

            <Tabs
              value={tab}
              onChange={(e, value) => setTab(value)}
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              <Tab label={`Matched (${matchedFixtures.length})`} />
              <Tab label={`Ours only (${unmatchedFixtures.length})`} />
              <Tab label={`Midnite only (${theirUnmatched.length})`} />
            </Tabs>

            {tab === 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Paired to a Midnite fixture. These are the fixtures the odds job polls.
                </Typography>
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Kick-off</TableCell>
                        <TableCell>Fixture</TableCell>
                        <TableCell>Midnite ID</TableCell>
                        <TableCell>Paired</TableCell>
                        <TableCell>Odds</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchedFixtures.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary">
                              No fixtures are paired yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {matchedFixtures.map((fixture) => (
                        <TableRow key={fixture.id} hover>
                          <TableCell>{formatDateTime(fixture.kickoffAt)}</TableCell>
                          <TableCell>
                            <Box sx={{ fontSize: 13 }}>
                              {fixture.homeName ?? "?"} v {fixture.awayName ?? "?"}
                            </Box>
                          </TableCell>
                          <TableCell>{fixture.midniteId}</TableCell>
                          <TableCell>{formatDateTime(fixture.midnitePairedAt)}</TableCell>
                          <TableCell>
                            {fixture.oddsMarketStatus ? (
                              <Tooltip
                                title={`Last fetched ${formatDateTime(fixture.oddsFetchedAt)}`}
                              >
                                <Chip
                                  label={fixture.oddsMarketStatus}
                                  size="small"
                                  color={
                                    fixture.oddsMarketStatus === "halted" ? "default" : "success"
                                  }
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                none yet
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {tab === 1 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Our fixtures with no Midnite pairing - these show no odds strip. Midnite only
                  publishes a rolling window of upcoming games, so most of a season is legitimately
                  unmatched. The highlighted rows are the ones worth a look: they fall inside the
                  window the last sync covered and still were not sent.
                </Typography>
                {syncCaption && (
                  <Typography variant="caption" color="text.secondary">
                    {syncCaption}
                  </Typography>
                )}
                <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Kick-off</TableCell>
                        <TableCell>Fixture</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmatchedFixtures.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="body2" color="text.secondary">
                              Every upcoming fixture is paired.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {unmatchedFixtures.map((fixture) => {
                        const flagged = inMidniteWindow(fixture.kickoffAt);
                        const row = (
                          <TableRow
                            key={fixture.id}
                            hover
                            sx={flagged ? { backgroundColor: "warning.lighter" } : undefined}
                          >
                            <TableCell>{formatDateTime(fixture.kickoffAt)}</TableCell>
                            <TableCell>
                              <Box sx={{ fontSize: 13 }}>
                                {fixture.homeName ?? "?"} v {fixture.awayName ?? "?"}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                        return flagged ? (
                          <Tooltip
                            key={fixture.id}
                            title={`Inside Midnite's last published window (${windowLabel}) but no entry resolved to it - check the Midnite only tab for a name or date mismatch.`}
                          >
                            {row}
                          </Tooltip>
                        ) : (
                          row
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {tab === 2 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Fixtures Midnite sent in the last sync that did not resolve to any of ours. Their
                  fixtures are not stored - this is the record of a sync run, so it changes only
                  when someone syncs again.
                </Typography>
                {syncCaption && (
                  <Typography variant="caption" color="text.secondary">
                    {syncCaption}
                  </Typography>
                )}
                {!lastSync ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No sync recorded yet - run Sync matches to see what Midnite is publishing.
                  </Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Start time</TableCell>
                          <TableCell>Midnite fixture</TableCell>
                          <TableCell>Midnite ID</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {theirUnmatched.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary">
                                The last sync placed every fixture Midnite sent.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {theirUnmatched.map((entry) => (
                          <TableRow key={entry.midniteId} hover>
                            <TableCell>{formatDateTime(entry.startTime)}</TableCell>
                            <TableCell>
                              <Box sx={{ fontSize: 13 }}>{entry.name}</Box>
                            </TableCell>
                            <TableCell>{entry.midniteId}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

MidniteOddsSection.propTypes = {
  onRunComplete: PropTypes.func,
};
